import { Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { StatusChip } from '@/components/ui/status-chip';
import { useCancelMmProgramJob, useMmProgramJobs } from '@/hooks/queries/mm-programs/use-mm-program-jobs';
import { getErrorMessage } from '@/lib/api/error';
import type { Job } from '@/services/api/mm-program-jobs';
import type { MmProgramSummary } from '@/services/api/mm-programs';
import { formatUtcDateTime } from '@/utils/format';
import { JobForm } from './job-form';
import { canCancel, describeJobTarget, describeProgress, JOB_TYPE_LABELS, jobStatusVariant } from './job-request';

const ErrorCell = ({ job }: { job: Job }) =>
  job.error ? (
    <details className="max-w-[28rem]">
      <summary className="cursor-pointer text-xs text-red-500">Error</summary>
      <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap text-[11px] text-muted-foreground">{job.error}</pre>
    </details>
  ) : null;
ErrorCell.displayName = 'ErrorCell';

const CancelCell = ({ job, onCancel }: { job: Job; onCancel: (job: Job) => void }) =>
  canCancel(job) ? (
    <Button onClick={() => onCancel(job)} size="sm" type="button" variant="outline">
      Cancel
    </Button>
  ) : null;
CancelCell.displayName = 'CancelCell';

const buildColumns = (onCancel: (job: Job) => void, canSubmit: boolean): ColumnDef<Job>[] => [
  { accessorKey: 'created_at', header: 'Queued', cell: ({ row }) => <DateCell format={formatUtcDateTime} value={row.original.created_at} /> },
  {
    id: 'job',
    header: 'Job',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{JOB_TYPE_LABELS[row.original.type]}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{describeJobTarget(row.original)}</span>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <StatusChip className="w-fit px-2 py-0.5 text-xs" variant={jobStatusVariant(row.original.status)}>
          {row.original.status}
        </StatusChip>
        <span className="text-xs text-muted-foreground">{describeProgress(row.original)}</span>
        <ErrorCell job={row.original} />
      </div>
    ),
  },
  {
    accessorKey: 'requested_by',
    header: 'Requested by',
    cell: ({ row }) => (
      <div className="flex max-w-[20rem] flex-col">
        <span>{row.original.requested_by}</span>
        {row.original.justification ? <span className="truncate text-xs text-muted-foreground">{row.original.justification}</span> : null}
      </div>
    ),
  },
  ...(canSubmit ? [{ id: 'actions', header: '', cell: ({ row }: { row: { original: Job } }) => <CancelCell job={row.original} onCancel={onCancel} /> }] : []),
];

interface JobsViewProps {
  marketMakers: MmProgramSummary[];
}

/** The schedule's runs and reports, backfills and reports sent again, queued for the mm-program instance. */
export const JobsView = ({ marketMakers }: JobsViewProps) => {
  const { data, isPending, isError, error, refetch } = useMmProgramJobs();
  const { mutateAsync: cancelJob } = useCancelMmProgramJob();
  const canSubmit = data?.canSubmit ?? false;

  const cancel = useCallback(
    async (job: Job) => {
      const confirmed = await confirmAction({
        title: `Cancel ${JOB_TYPE_LABELS[job.type].toLowerCase()}`,
        actionLabel: 'Cancel job',
        destructive: true,
        requireJustification: false,
        onConfirm: () => cancelJob(job.id),
      });
      if (confirmed !== null) toast.success(job.status === 'queued' ? 'Job cancelled.' : 'The job stops after its current step.');
    },
    [cancelJob],
  );
  // Kept across the list's refreshes, which DataTable would otherwise take for new columns.
  const columns = useMemo(() => buildColumns(cancel, canSubmit), [cancel, canSubmit]);

  return (
    <div className="flex flex-col gap-6">
      {isError ? <QueryErrorBanner message={`Failed to fetch jobs: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}
      {canSubmit ? <JobForm marketMakers={marketMakers} /> : null}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={data?.jobs} emptyMessage="No jobs yet." isLoading={isPending} pageSize={20} surface={false} />
        </CardContent>
      </Card>
    </div>
  );
};
JobsView.displayName = 'JobsView';
